// PHI-safe carrier for the coverage-check -> registration journey (sessionStorage, one tab).
// Values held here (name/DOB/gender/ZIP) are PHI: never log, no analytics payload, no URL.
// The flow gate keys off an explicit `flow` marker, not "some data exists", so a stale/foreign
// write without the marker is ignored and a branded/270 user keeps the existing targets.

import {
  CoverageCheckValues,
  GenericEligibilityCheckResult,
  CompleteAccountMode,
  ProviderType,
} from '../interfaces/eligibility';

const STORE_KEY = 'COVERAGE_CHECK_STORE';

// Presence of this exact value is the flow gate, not non-null data.
export const COVERAGE_CHECK_FLOW = 'coverage-check' as const;

// Verified 270/271 coverage (guest check token + matched affiliationId, both PHI-free) carried
// into account creation so the backend links the new patient to it. CSV carries nothing.
export interface CoverageEligibility {
  type: '270';
  token: string;
  affiliationId: number;
  // routes the Benefits CTAs to sign-in instead of account creation (boolean only, non-PHI)
  existingAccount?: boolean;
}

interface CoverageCheckStore {
  flow: typeof COVERAGE_CHECK_FLOW;
  values?: CoverageCheckValues;
  result?: GenericEligibilityCheckResult;
  mode?: CompleteAccountMode;
  eligibility?: CoverageEligibility;
}

const hasSessionStorage = (): boolean =>
  typeof window !== 'undefined' && Boolean(window.sessionStorage);

// Returns null on absence, malformed JSON, or a missing/invalid flow marker.
const read = (): CoverageCheckStore | null => {
  if (!hasSessionStorage()) {
    return null;
  }
  const raw = window.sessionStorage.getItem(STORE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.flow !== COVERAGE_CHECK_FLOW) {
      return null;
    }
    return parsed as CoverageCheckStore;
  } catch {
    return null;
  }
};

const write = (store: CoverageCheckStore): void => {
  if (!hasSessionStorage()) {
    return;
  }
  window.sessionStorage.setItem(STORE_KEY, JSON.stringify(store));
};

// Seed the carrier with the coverage-check submission.
export const setCoverageData = (
  values: CoverageCheckValues,
  result: GenericEligibilityCheckResult
): void => {
  const current = read() ?? { flow: COVERAGE_CHECK_FLOW };
  write({ ...current, flow: COVERAGE_CHECK_FLOW, values, result });
};

export const getCoverageData = (): CoverageCheckStore | null => read();

// Overlay the verified-270 affiliation's priced provider types onto the carried result, so the
// coverage-benefits screen renders tiles for a 270 user who isn't in the CSV roster.
export const setCoverageProviderTypes = (
  providerTypes: ProviderType[]
): void => {
  const current = read() ?? { flow: COVERAGE_CHECK_FLOW };
  const result: GenericEligibilityCheckResult = {
    covered: current.result?.covered ?? true,
    requiresRegistration: current.result?.requiresRegistration ?? true,
    providerTypes,
  };
  write({ ...current, flow: COVERAGE_CHECK_FLOW, result });
};

// Set how Complete Account should treat the carried identity fields.
export const setCompleteAccountMode = (mode: CompleteAccountMode): void => {
  const current = read() ?? { flow: COVERAGE_CHECK_FLOW };
  write({ ...current, flow: COVERAGE_CHECK_FLOW, mode });
};

export const getCompleteAccountMode = (): CompleteAccountMode | null =>
  read()?.mode ?? null;

// Record the verified 270/271 coverage for the onward registration step.
export const setCoverageEligibility = (
  eligibility: CoverageEligibility
): void => {
  const current = read() ?? { flow: COVERAGE_CHECK_FLOW };
  write({ ...current, flow: COVERAGE_CHECK_FLOW, eligibility });
};

export const getCoverageEligibility = (): CoverageEligibility | null =>
  read()?.eligibility ?? null;

// Gate the shared register components consult before re-pointing their targets.
export const isCoverageCheckFlow = (): boolean => read() !== null;

export const clear = (): void => {
  if (!hasSessionStorage()) {
    return;
  }
  window.sessionStorage.removeItem(STORE_KEY);
};
